import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { mutate } from "swr";

import CalInlineEmbed from "components/CalInlineEmbed";
import Loading from "components/Loading";
import PageHeader from "components/PageHeader";
import ViewAsCustomerToggle from "components/ViewAsCustomerToggle";
import { CONSULTATION_CAL_LINK } from "constants/at-need";
import { useUser } from "context/UserContext";
import { useGET } from "hooks/useAPI";
import { TASK_NAMES } from "hooks/useCaseTasksAPI";
import { useIsCareSpecialist } from "hooks/useIsCareSpecialist";
import { getArrangementStatus } from "helpers/at-need";
import { setTaskOptimism } from "helpers/taskOptimism";
import { PortalContent } from "layouts/PortalLayout";
import {
  getArrangementHubStatus,
  getConsultationHubStatus,
  getVitalsHubStatus,
  hasScheduledConsultation,
} from "pages/CaseDetails/startHubStatus";
import { useHubTaskStates } from "pages/CaseDetails/useHubTaskStates";

type VitalsSubmissionsResponse = {
  results?: unknown[];
};

function SchedulingFrame({ children }: { children: ReactNode }) {
  return (
    <>
      <PortalContent>{children}</PortalContent>
      <ViewAsCustomerToggle />
    </>
  );
}

function ConsultationSchedulingError() {
  return (
    <div className="mx-auto flex min-h-[60vh] w-full max-w-xl flex-col items-center justify-center px-4 text-center">
      <h1 className="font-serif text-[34px] font-normal leading-[1.15] text-soil">
        We could not load scheduling
      </h1>
      <p className="mt-3 text-base text-oat-700">
        Please return to your case and try again.
      </p>
      <Link
        to=".."
        relative="path"
        className="mt-6 rounded-lg bg-cantelope px-5 py-3 text-sm font-bold text-soil hover:bg-cantelope/80"
      >
        Back to your case
      </Link>
    </div>
  );
}

function useRequiredCaseId(): string {
  const { caseId } = useParams<{ caseId: string }>();
  return caseId as string;
}

export default function ConsultationScheduling() {
  const caseId = useRequiredCaseId();
  const navigate = useNavigate();
  const { user } = useUser();
  const isCareSpecialist = useIsCareSpecialist();
  const [hasLoadError, setHasLoadError] = useState(false);

  const appDataRoute = `/cases/${caseId}/app-data`;
  const vitalsRoute = `/cases/${caseId}/vitals/submissions`;

  const {
    data: appDataRaw,
    error: appDataError,
    isLoading: isLoadingAppData,
  } = useGET(appDataRoute, undefined, {
    shouldRetryOnError: (error: any) => error?.statusCode !== 404,
  });
  const {
    data: vitalsSubmissionsData,
    error: vitalsSubmissionsError,
    isLoading: isLoadingVitals,
  } = useGET(vitalsRoute, undefined, {
    shouldRetryOnError: (error: any) => error?.statusCode !== 404,
  });
  const { data: caseData, mutate: revalidateCase } = useGET(`/cases/${caseId}`);
  const detailedCase = caseData as { stage?: string } | undefined;

  const {
    vitalsTaskState,
    arrangementTaskState,
    consultationTaskState,
    isLoadingTasks,
  } = useHubTaskStates(caseId, { enabled: true });

  // The scheduling decision below navigates based on the case stage, so it
  // must not run against a stale cached stage: only a case response received
  // after mount counts as fresh, and an unverifiable stage fails closed.
  const [caseStageCheck, setCaseStageCheck] = useState<
    "pending" | "fresh" | "failed"
  >("pending");
  useEffect(() => {
    let active = true;
    setCaseStageCheck("pending");
    const fail = () => {
      if (active) {
        setCaseStageCheck((current) =>
          current === "pending" ? "failed" : current,
        );
      }
    };
    // A hung case fetch must not strand the family on Loading.
    const timeoutId = window.setTimeout(fail, 10000);
    revalidateCase().then(() => {
      if (active) setCaseStageCheck("fresh");
    }, fail);
    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [revalidateCase, caseId]);

  const namespace = useMemo(
    () => `case-consultation-${caseId.replace(/[^A-Za-z0-9_-]/g, "-")}`,
    [caseId],
  );
  const customerName = useMemo(
    () => [user?.first_name, user?.last_name].filter(Boolean).join(" "),
    [user?.first_name, user?.last_name],
  );
  const config = useMemo(() => {
    const nextConfig: Record<string, string> = {
      "metadata[reference_type]": "case",
      "metadata[reference_id]": caseId,
      layout: "month_view",
    };
    if (customerName) nextConfig.name = customerName;
    if (user?.email) nextConfig.email = user.email;
    return nextConfig;
  }, [caseId, customerName, user?.email]);

  const backHref = `/cases/${caseId}`;
  const isAppDataNotFound =
    (appDataError as { statusCode?: number } | undefined)?.statusCode === 404;
  const hasVitalsSubmission =
    (
      (vitalsSubmissionsData as VitalsSubmissionsResponse | undefined)
        ?.results ?? []
    ).length > 0;
  const vitalsStatus = getVitalsHubStatus({
    hasSubmission: hasVitalsSubmission,
    taskState: vitalsTaskState,
  });
  const arrangementStatus = getArrangementHubStatus(
    isAppDataNotFound ? null : getArrangementStatus(appDataRaw),
    arrangementTaskState,
  );
  const consultationStatus = getConsultationHubStatus({
    hasConsultationBooking: hasScheduledConsultation(appDataRaw),
    vitalsStatus,
    arrangementStatus,
    stage: detailedCase?.stage,
    taskState: consultationTaskState,
  });

  const handleBookingSuccess = useCallback(() => {
    setTaskOptimism(caseId, TASK_NAMES.scheduleConsultation);
    void mutate(`/cases/${caseId}/app-data`);
    navigate(backHref, {
      replace: true,
      state: { consultationBooked: true, showCompletedStartHub: true },
    });
  }, [backHref, caseId, navigate]);
  const handleLoadError = useCallback(() => {
    setHasLoadError(true);
  }, []);

  if (isCareSpecialist) {
    return <Navigate to={backHref} replace />;
  }

  if (
    isLoadingAppData ||
    isLoadingVitals ||
    isLoadingTasks ||
    caseStageCheck === "pending"
  ) {
    return (
      <SchedulingFrame>
        <Loading message="Loading scheduling..." />
      </SchedulingFrame>
    );
  }

  if (
    caseStageCheck === "failed" ||
    (!!appDataError && !isAppDataNotFound) ||
    (!!vitalsSubmissionsError &&
      (vitalsSubmissionsError as { statusCode?: number }).statusCode !== 404)
  ) {
    return (
      <SchedulingFrame>
        <ConsultationSchedulingError />
      </SchedulingFrame>
    );
  }

  if (consultationStatus !== "start") {
    return <Navigate to={backHref} replace />;
  }

  return (
    <SchedulingFrame>
      <div className="flex w-full flex-col">
        <PageHeader
          backLink={{ to: backHref, label: "Back" }}
          title="Schedule your Consultation"
          description="Pick a time to speak with your dedicated Care Specialist."
        />

        <section aria-label="Consultation calendar" className="mt-8 w-full">
          <CalInlineEmbed
            namespace={namespace}
            calLink={CONSULTATION_CAL_LINK}
            config={config}
            onBookingSuccess={handleBookingSuccess}
            onLoadError={handleLoadError}
          />
          {hasLoadError && (
            <p className="mt-3 text-center text-base text-soil/70">
              We could not load scheduling. Please refresh and try again.
            </p>
          )}
        </section>
      </div>
    </SchedulingFrame>
  );
}
